// PayAnalysisEngine.js - Core calculation logic with modular pay table support
import { indotPayTable } from '../payTables/indotPayTable';

// Pay table registry - add new pay tables here
const PAY_TABLE_REGISTRY = {
  'INDOT': indotPayTable,
  // 'TXDOT': txdotPayTable,  // Future additions
  // 'CALTRANS': caltransPayTable,
};

export class PayAnalysisEngine {
  constructor(segments, payTableName, projectConfig) {
    this.segments = segments || [];
    this.config = projectConfig || {};
    
    // Get the appropriate pay table utility
    this.payTable = this._getPayTableUtility(payTableName);
    if (!this.payTable) {
      console.warn(`Pay table "${payTableName}" not found, using INDOT as fallback`);
      this.payTable = indotPayTable;
    }

    // Validate configuration
    const validation = this.payTable.validateConfiguration(this.config);
    if (!validation.isValid) {
      console.warn('Configuration validation failed:', validation.errors);
    }
    if (validation.warnings && validation.warnings.length > 0) {
      console.info('Configuration warnings:', validation.warnings);
    }
  }

  // Get pay table utility by name
  _getPayTableUtility(payTableName) {
    if (!payTableName) return null;
    return PAY_TABLE_REGISTRY[payTableName.toUpperCase()];
  }

  // Use segments directly as lots - no artificial lot creation
  getSegmentLots() {
    // Filter out segments without valid IRI data for payment analysis
    const validSegments = this.segments.filter(segment => {
      return segment.iri_avg_in_per_mi !== null && 
             segment.iri_avg_in_per_mi !== undefined &&
             !segment.exception &&
             segment.distance_ft > 0;
    });

    // Convert each valid segment to a lot
    return validSegments.map((segment, index) => ({
      lot_number: index + 1,
      start_station: parseFloat(segment.from_station_ft),
      end_station: parseFloat(segment.to_station_ft),
      length_ft: parseFloat(segment.distance_ft),
      actual_length_ft: parseFloat(segment.distance_ft), // Same as length_ft for segments
      mri: parseFloat(segment.iri_avg_in_per_mi),
      segment: segment
    }));
  }

  // Calculate payment for a single segment/lot using the pay table utility
  calculateSegmentPayment(segmentLot) {
    // Prepare lot data for the pay table utility
    const lotData = {
      mri: segmentLot.mri,
      lengthFt: segmentLot.actual_length_ft,
      startStation: segmentLot.start_station,
      endStation: segmentLot.end_station,
      lotNumber: segmentLot.lot_number
    };

    // Use the pay table utility for detailed calculation
    const paymentResult = this.payTable.calculateLotPayment(lotData, this.config);

    // Add segment-specific data
    return {
      ...paymentResult,
      segment: segmentLot.segment,
      segment_id: segmentLot.segment.segment_id,
      
      // Include both theoretical and actual lengths (same for segments)
      theoretical_length_ft: segmentLot.length_ft,
      actual_length_ft: segmentLot.actual_length_ft,
      length_coverage_percent: 100, // Always 100% for individual segments
      
      // Additional segment data
      iri_left: parseFloat(segmentLot.segment.iri_left_in_per_mi) || null,
      iri_right: parseFloat(segmentLot.segment.iri_right_in_per_mi) || null,
      avg_speed_mph: parseFloat(segmentLot.segment.avg_speed_mph) || null,
      localized_roughness_count: segmentLot.segment.localized_roughness_count || null,
      
      // Legacy compatibility fields
      condition: this._mapQualityToLegacyCondition(paymentResult.qualityCategory),
      exceeds_threshold: paymentResult.exceedsThreshold
    };
  }

  // Generate complete pay analysis using segment-based approach
  generatePayAnalysis() {
    const segmentLots = this.getSegmentLots();
    const lotResults = segmentLots.map(lot => this.calculateSegmentPayment(lot));
    
    // Use the pay table utility for summary calculations
    const summary = this.payTable.calculateProjectSummary(lotResults);

    // Add segment-specific metrics
    const totalSegments = this.segments.length;
    const validSegments = lotResults.length;
    const exceptionSegments = this.segments.filter(s => s.exception).length;

    return {
      lots: lotResults,
      summary: {
        // Enhanced summary from pay table utility
        ...summary,
        
        // Segment analysis metrics
        total_segments: totalSegments,
        valid_segments: validSegments,
        exception_segments: exceptionSegments,
        segment_validity_percent: totalSegments > 0 ? (validSegments / totalSegments) * 100 : 0,
        
        // Length analysis is straightforward for segments
        total_theoretical_length_ft: summary.totalLengthFt,
        total_actual_length_ft: summary.totalLengthFt,
        length_coverage_percent: 100, // Always 100% for segment-based analysis
        avg_lot_coverage_percent: 100,
        
        // Additional legacy fields for backward compatibility
        total_lots: summary.totalLots,
        total_length_ft: summary.totalLengthFt,
        total_length_miles: summary.totalLengthMiles,
        total_tonnage: summary.totalTonnage,
        total_base_payment: summary.totalBasePayment,
        total_adjusted_payment: summary.totalAdjustedPayment,
        total_dollar_adjustment: summary.totalDollarAdjustment,
        
        min_pay_factor: summary.minPayFactor,
        max_pay_factor: summary.maxPayFactor,
        avg_pay_factor: summary.avgPayFactor,
        
        bonus_lots: summary.bonusLots,
        penalty_lots: summary.penaltyLots,
        neutral_lots: summary.neutralLots,
        exceeds_threshold_lots: summary.thresholdExceededLots,
        
        avg_mri: summary.avgMri,
        overall_condition: this._mapQualityToLegacyCondition(summary.overallQuality),
        
        bonus_percentage: summary.bonusPercentage,
        penalty_percentage: summary.penaltyPercentage,
        threshold_exceedance_percentage: summary.thresholdExceedancePercentage,

        // New enhanced fields
        correction_required_lots: summary.correctionRequiredLots,
        correction_percentage: summary.correctionPercentage,
        project_compliance: summary.projectCompliance,
        mri_standard_deviation: summary.mriStandardDeviation
      },

      // Analysis metadata
      metadata: {
        pay_table: this.payTable.name,
        pay_table_description: this.payTable.description,
        analysis_type: 'segment_based',
        analysis_date: new Date().toISOString(),
        total_segments_analyzed: validSegments,
        config_validation: this.payTable.validateConfiguration(this.config),
        uses_actual_segment_lengths: true
      }
    };
  }

  // Generate data for export (leveraging pay table utility)
  generateExportData() {
    const analysis = this.generatePayAnalysis();
    
    // Use pay table-specific export format if available
    if (typeof this.payTable.generateExportData === 'function') {
      return this.payTable.generateExportData(analysis, this.config);
    }

    // Fallback to generic export format with segment data
    return {
      lots_data: analysis.lots.map(lot => ({
        lot_number: lot.lotNumber,
        segment_id: lot.segment_id,
        start_station_ft: lot.startStation,
        end_station_ft: lot.endStation,
        length_ft: lot.actual_length_ft,
        mri_in_per_mi: lot.mri,
        iri_left: lot.iri_left,
        iri_right: lot.iri_right,
        pay_factor: lot.payFactor,
        tonnage: lot.tonnage,
        base_payment: lot.basePayment,
        adjusted_payment: lot.adjustedPayment,
        dollar_adjustment: lot.dollarAdjustment,
        requires_correction: lot.requiresCorrection,
        target_mri: lot.targetMri,
        note: lot.note,
        avg_speed_mph: lot.avg_speed_mph,
        localized_roughness_count: lot.localized_roughness_count
      })),
      
      summary_data: {
        project_name: this.config.project_name || 'Unknown Project',
        pay_table: this.payTable.name,
        analysis_type: 'segment_based',
        total_segments: analysis.summary.total_segments,
        valid_segments: analysis.summary.valid_segments,
        exception_segments: analysis.summary.exception_segments,
        total_adjustment: analysis.summary.totalDollarAdjustment,
        avg_mri: analysis.summary.avgMri,
        project_compliance: analysis.summary.projectCompliance,
        analysis_config: this.config
      }
    };
  }

  // Enhanced lot filtering and analysis methods
  getLotsRequiringCorrection() {
    const analysis = this.generatePayAnalysis();
    return analysis.lots.filter(lot => lot.requiresCorrection);
  }

  getLotsByPayCategory(category) {
    const analysis = this.generatePayAnalysis();
    return analysis.lots.filter(lot => lot.payCategory === category);
  }

  getQualityDistribution() {
    const analysis = this.generatePayAnalysis();
    const distribution = {};
    
    analysis.lots.forEach(lot => {
      const category = lot.qualityCategory;
      distribution[category] = (distribution[category] || 0) + 1;
    });

    return distribution;
  }

  // Get segments that were excluded from analysis
  getExceptionSegments() {
    return this.segments.filter(segment => 
      segment.exception || 
      segment.iri_avg_in_per_mi === null || 
      segment.iri_avg_in_per_mi === undefined ||
      segment.distance_ft <= 0
    );
  }

  // Calculate potential savings if corrections were made
  calculateCorrectionBenefit() {
    const correctionLots = this.getLotsRequiringCorrection();
    let potentialSavings = 0;

    correctionLots.forEach(lot => {
      // Calculate what payment would be at target MRI
      const targetPayDetails = this.payTable.getPayDetails(
        lot.targetMri, 
        lot.pavementType
      );
      
      const improvedPayment = lot.basePayment * targetPayDetails.payFactor;
      const currentPayment = lot.adjustedPayment;
      potentialSavings += (improvedPayment - currentPayment);
    });

    return {
      lots_requiring_correction: correctionLots.length,
      current_total_penalty: correctionLots.reduce((sum, lot) => sum + lot.dollarAdjustment, 0),
      potential_total_savings: potentialSavings,
      net_benefit: potentialSavings - correctionLots.reduce((sum, lot) => sum + lot.dollarAdjustment, 0)
    };
  }

  // Helper methods for backward compatibility
  _mapQualityToLegacyCondition(qualityCategory) {
    const mapping = {
      'excellent': 'Excellent',
      'good': 'Good',
      'fair': 'Fair', 
      'poor': 'Poor',
      'very_poor': 'Very Poor'
    };
    return mapping[qualityCategory] || 'Unknown';
  }

  // Static method to get available pay tables
  static getAvailablePayTables() {
    return Object.keys(PAY_TABLE_REGISTRY).map(name => ({
      name: name,
      description: PAY_TABLE_REGISTRY[name].description,
      utility: PAY_TABLE_REGISTRY[name]
    }));
  }

  // Static method to validate a pay table exists
  static validatePayTableName(payTableName) {
    return payTableName && PAY_TABLE_REGISTRY[payTableName.toUpperCase()] !== undefined;
  }
}